import { useLayoutEffect, useState, useRef } from 'react';

interface UseContentOverflowOptions {
  // When false, measuring pauses and the last result is kept (e.g. while the content is shown in full)
  enabled?: boolean;
}

/**
 * Detects if content overflows its container by comparing scrollHeight vs clientHeight.
 * The container should have overflow:hidden and a constrained height.
 * Automatically re-checks on resize.
 */
export const useContentOverflow = ({ enabled = true }: UseContentOverflowOptions = {}) => {
  const [isOverflowing, setIsOverflowing] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container || !enabled) {
      return;
    }

    const checkOverflow = () => {
      const { scrollHeight, clientHeight } = container;
      setIsOverflowing(scrollHeight > clientHeight);
    };

    // Measure now so the first paint is right, then again after the browser has laid out.
    checkOverflow();
    requestAnimationFrame(checkOverflow);

    const resizeObserver = new ResizeObserver(() => {
      // Also wait for next frame after resize to ensure layout has been recalculated.
      requestAnimationFrame(checkOverflow);
    });
    resizeObserver.observe(container);

    return () => resizeObserver.disconnect();
  }, [enabled]);

  return { containerRef, isOverflowing };
};
